import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { CheckCircle, Login as LoginIcon, HourglassEmpty, Business } from '@mui/icons-material';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

// Styled Components
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  padding: '2rem 1rem',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(3),
  boxShadow: '0 10px 40px rgba(41, 30, 108, 0.15)',
  textAlign: 'center',
  width: '100%',
  backgroundColor: '#ffffff',
  animation: `${fadeIn} 0.6s ease-out`,
  border: '1px solid rgba(41, 30, 108, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: 'linear-gradient(90deg, #291e6c 0%, #4a3a9c 100%)',
  },
}));

const SuccessIcon = styled(CheckCircle)(({ theme }) => ({
  fontSize: 100,
  color: '#4caf50',
  animation: `${scaleIn} 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)`,
  filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))',
}));

const RegistrationCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(135deg, rgba(41, 30, 108, 0.05) 0%, rgba(41, 30, 108, 0.02) 100%)',
  border: '2px solid rgba(41, 30, 108, 0.15)',
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  animation: `${fadeIn} 0.8s ease-out`,
}));

const PendingCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(4),
  background: 'linear-gradient(135deg, #fff8e1 0%, #fffbf0 100%)',
  border: '2px solid #ffc107',
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  animation: `${fadeIn} 1s ease-out`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.8, 5),
  borderRadius: theme.spacing(1.5),
  fontSize: '1.1rem',
  fontWeight: 700,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
  backgroundColor: '#291e6c',
  color: 'white',
  boxShadow: '0 4px 12px rgba(41, 30, 108, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#1f1750',
    boxShadow: '0 6px 20px rgba(41, 30, 108, 0.4)',
    transform: 'translateY(-2px)',
  },
}));

const StateAssociationSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationNumber = location.state?.registrationNumber || 'N/A';

  return (
    <PageWrapper>
      <Container>
        <StyledPaper elevation={3}>
          {/* Success Icon */}
          <Box sx={{ mb: 3 }}>
            <SuccessIcon />
          </Box>

          {/* Main Heading */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: '#291e6c',
              fontFamily: "'Oswald', sans-serif",
              mb: 2,
              letterSpacing: '0.5px',
            }}
          >
            Registration Request Successful!
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 3,
              fontSize: '1.05rem',
              lineHeight: 1.6,
            }}
          >
            Your state association registration request with IWLF has been received.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Registration Number Card */}
          {/* <RegistrationCard>
            <CardContent sx={{ py: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1.5 }}>
                <Business sx={{ color: '#291e6c', fontSize: 28 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                >
                  Registration Number
                </Typography>
              </Stack>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#291e6c',
                  fontFamily: "'Oswald', sans-serif",
                  letterSpacing: '1.5px',
                  fontSize: '2rem',
                }}
              >
                {registrationNumber}
              </Typography>
            </CardContent>
          </RegistrationCard> */}

          {/* Pending Verification Alert */}
          <PendingCard>
            <CardContent sx={{ py: 3 }}>
              <HourglassEmpty
                sx={{
                  fontSize: 50,
                  color: '#f57c00',
                  mb: 1.5,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: '#e65100',
                  fontWeight: 700,
                  mb: 1,
                  fontFamily: "'Oswald', sans-serif",
                }}
              >
                Pending Admin Verification
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#ef6c00',
                  fontWeight: 500,
                  lineHeight: 1.6,
                }}
              >
                Your application is currently under review. You will receive an email notification once your application has been verified by our administrators.
              </Typography>
            </CardContent>
          </PendingCard>

          {/* Call to Action */}
          <StyledButton
            variant="contained"
            onClick={() => navigate('/state-association/dashboard')}
            startIcon={<LoginIcon />}
            fullWidth
            size="large"
          >
            Go to Dashboard
          </StyledButton>

          {/* Additional Info */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 3,
              color: 'text.secondary',
              fontSize: '0.85rem',
            }}
          >
            Please check your email for further instructions. You can login after your application is approved.
          </Typography>
        </StyledPaper>
      </Container>
    </PageWrapper>
  );
};

export default StateAssociationSuccess;
